import React, { useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useUploadedImages } from "../../hooks";
import { Button, Card } from "react-bootstrap";
import "./ImageMatcher.scss";
import { PageBody, PageHeader, PageLayout } from "../../components";
import { IDropFileModel } from "../../types/common";
import { imageMatcherUploadedPageURL } from "../../utils/imageMatcherUtil";

const ACCEPTED_FILES_LIMIT = 50;
const ACCEPTED_FILES_TYPE = { "image/*": [] };

const ImageMatcherUploadPage: React.FC = () => {
    const { t } = useTranslation();
    const { handleSetUploadedImages, handleClearUploadedImages } = useUploadedImages();
    const navigate = useNavigate();

    const setResults = useCallback(async (acceptedFiles: File[]) => {
        const dropFiles: IDropFileModel[] = acceptedFiles.map((file) => ({
            fileobject: file,
            preview: URL.createObjectURL(file),
        }));
        await handleSetUploadedImages(dropFiles);
        navigate(imageMatcherUploadedPageURL, { state: dropFiles, replace: true });
    }, [navigate]
    );

    const handlePaste = useCallback(
        (event: ClipboardEvent) => {
            const items = event.clipboardData?.items || [];
            const acceptedFiles: File[] = Array.from(items)
                .filter(item => item.kind === "file" && item.type.startsWith("image/"))
                .map(item => item.getAsFile() as File);
            if (acceptedFiles.length > ACCEPTED_FILES_LIMIT) {
                toast(t("DropZone.maxFilesExceeded").replace("{}", ACCEPTED_FILES_LIMIT.toString()), { type: "error" });
            } else {
                setResults(acceptedFiles);
            }
        },
        [setResults, t]
    );

    useEffect(() => {
        handleClearUploadedImages();
    }, []);

    useEffect(() => {
        document.addEventListener("paste", handlePaste);
        return () => document.removeEventListener("paste", handlePaste);
    }, [handlePaste]);


    const { getRootProps, getInputProps } = useDropzone({
        accept: ACCEPTED_FILES_TYPE,
        maxFiles: ACCEPTED_FILES_LIMIT,
        onDrop: setResults,
        onDropRejected: (fileRejections) => {
            let errorMessage = ""
            fileRejections.forEach(({ errors }) => {
                errors.forEach((error) => {
                    switch (error.code) {
                        case "file-invalid-type":
                            errorMessage = t("DropZone.invalidFileType");
                            break;
                        case "too-many-files":
                            errorMessage = t("DropZone.maxFilesExceeded").replace("{}", ACCEPTED_FILES_LIMIT.toString());
                            break;
                        default:
                            errorMessage = "Unknown"
                            break;
                    }
                });
            });
            toast(errorMessage, { type: "error" });
        },
    });

    return (
        <PageLayout>
            <PageHeader title={t("ExtractedImages.ImageMatcher")} t={t} />
            <PageBody t={t}>
                <div className="d-flex justify-content-center align-items-center image-matcher-upload-container">
                    <Card className="border-0">
                        <Card.Body {...getRootProps()} className="dropzone">
                            <input {...getInputProps()} />
                            <Card.Text>{t("DropZone.text")}</Card.Text>
                            <Card.Text>{t("DropZone.clipBoardText")}</Card.Text>
                            <Button variant="secondary" className="mt-2" type="button">
                                {t("Button.selectFile")}
                            </Button>
                        </Card.Body>
                    </Card>
                </div>
            </PageBody>
        </PageLayout>
    )
};

export default ImageMatcherUploadPage;
